/** @jsxImportSource @emotion/react */
import React, { useEffect, useId, useState } from 'react';
import { theme } from 'styles/theme/theme';
import type { DivProps } from 'typings';
import Text from 'ud-ui/components/text';
import * as S from './styles';

type Status = 'shading' | 'primary' | 'secondary';

export interface StatusItem {
  label: string;
  status: Status;
  count: number;
}

interface IProps extends DivProps {
  data: StatusItem[];
  /** Статус, чья доля от общего числа выводится числом в центре кольца (обычно «завершено»). */
  highlightStatus?: Status;
}

const RADIUS = 42;
const STROKE_WIDTH = 14;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** Зазор между сегментами кольца, в единицах viewBox — тот же приём, что 2px между сегментами линейного бара. */
const SEGMENT_GAP = 5;

/** «В работе» — зелёный, «завершено» — красный: один цвет на статус и в кольце, и в легенде, и в числе. */
const COLOR_BY_STATUS: Record<Exclude<Status, 'shading'>, string> = {
  primary: theme.colors.CaseSuccess,
  secondary: theme.colors.CaseError,
};

/**
 * Кольцевая диаграмма распределения турниров по статусам — сменила линейный сегментированный
 * бар (IMGM-1478): те же три статуса и та же штриховка для «подготовки», но в форме, которая
 * оставляет место для процента завершённых турниров в центре.
 *
 * Кольцо строится тремя перекрывающимися `<circle>` с `strokeDasharray`/`strokeDashoffset`,
 * а не одним path — так «зазор» между сегментами получается закруглёнными концами `strokeLinecap`
 * без ручной геометрии дуг.
 *
 * Цвет статуса един для сегмента кольца, маркера легенды и числа справа (`COLOR_BY_STATUS`); подпись
 * в центре берёт цвет статуса `highlightStatus`, чью долю показывает.
 */
function StatusDistributionBar(props: IProps) {
  const { data, highlightStatus = 'secondary', ...rest } = props;
  const [filled, setFilled] = useState(false);
  const patternId = useId();

  useEffect(() => {
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => setFilled(true));
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, []);

  const total = data.reduce((sum, item) => sum + item.count, 0);
  const highlighted = data.find((item) => item.status === highlightStatus);
  const highlightPercent = total > 0 && highlighted ? Math.round((highlighted.count / total) * 100) : 0;
  const highlightColor = highlightStatus === 'shading' ? theme.colors.DarkBlue : COLOR_BY_STATUS[highlightStatus];

  let cumulative = 0;
  const segments = data.map((item) => {
    const fraction = total > 0 ? item.count / total : 0;
    const fullLength = fraction * CIRCUMFERENCE;
    const visibleLength = Math.max(fullLength - SEGMENT_GAP, 0);
    const rotationDeg = -90 + (cumulative / CIRCUMFERENCE) * 360;
    cumulative += fullLength;

    return { ...item, visibleLength, rotationDeg };
  });

  return (
    <div {...rest}>
      <S.Layout>
        <S.RingWrapper>
          <svg viewBox="0 0 120 120" width={112} height={112}>
            <defs>
              <pattern id={patternId} patternUnits="userSpaceOnUse" width={8} height={8} patternTransform="rotate(135)">
                <rect width={8} height={8} fill={theme.colors.SurfaceWhite} />
                <line x1={0} y1={0} x2={0} y2={8} stroke={theme.colors.WhiteGray60} strokeWidth={3} />
              </pattern>
            </defs>

            <circle cx={60} cy={60} r={RADIUS} fill="none" stroke={theme.colors.WhiteGray10} strokeWidth={STROKE_WIDTH} />

            {segments.map((segment, index) => {
              const stroke = segment.status === 'shading' ? `url(#${patternId})` : COLOR_BY_STATUS[segment.status];

              return (
                <circle
                  key={segment.status}
                  cx={60}
                  cy={60}
                  r={RADIUS}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={STROKE_WIDTH}
                  strokeLinecap="round"
                  strokeDasharray={`${CIRCUMFERENCE} ${CIRCUMFERENCE}`}
                  strokeDashoffset={filled ? CIRCUMFERENCE - segment.visibleLength : CIRCUMFERENCE}
                  style={{
                    transform: `rotate(${segment.rotationDeg}deg)`,
                    transformOrigin: '60px 60px',
                    transition: `stroke-dashoffset 1.6s cubic-bezier(0.16, 1, 0.3, 1) ${index * 220}ms, stroke 0.3s ease`,
                  }}
                />
              );
            })}
          </svg>

          <S.RingCenter>
            <Text variant="title-3" color={highlightColor}>
              {highlightPercent}%
            </Text>
          </S.RingCenter>
        </S.RingWrapper>

        <S.CountList>
          {data.map(({ label, status, count }) => (
            <S.CountRow key={status}>
              <S.LegendItem>
                <S.LegendColor className={status} />
                <Text variant="subtitle-1" color={theme.colors.Gray}>
                  {label}
                </Text>
              </S.LegendItem>
              <Text variant="subtitle-1" color={status === 'shading' ? theme.colors.DarkBlue : COLOR_BY_STATUS[status]}>
                {count}
              </Text>
            </S.CountRow>
          ))}
        </S.CountList>
      </S.Layout>
    </div>
  );
}

export default StatusDistributionBar;
